/**
 * Colouring a chart by a third column: the plan, the buckets, and the drawing.
 *
 * One module because the three belong together and three callers need them:
 * the single line chart, the ship grid, and the period-comparison grid. The
 * single scatter chart keeps its own older copy in `render-scatter.ts`, which
 * also owns the on-canvas discrete legend and the point-size handling that grew
 * up with it; merging the two is a tidy-up for another day, and the rules here
 * were written to match it.
 *
 * **The plan is made once for every panel that will be drawn, never per panel.**
 * The same value must be the same colour in each panel, otherwise comparing
 * panels - the reason the grid exists - means nothing.
 */

import type uPlot from 'uplot';
import {
  DISCRETE_SCALE,
  PLOT_COLORS,
  distinctColorValues,
  formatColorValue,
  isDiscreteScale,
} from '../plot-constants.ts';
import { colorScaleRgb, discreteColors } from './color-scale.ts';
import type { PlotResponse } from '../../../api/types.ts';

export interface NumericColorDomain {
  min: number;
  max: number;
  colorBy: string;
}

export interface NumericBuckets {
  colors: string[];
  indices: number[][];
}

/**
 * How a scatter grid colours its points, decided once for the whole grid.
 *
 * Per panel would be wrong in the way that matters: the same load value would
 * be a different colour in each ship's panel, and comparing the panels is the
 * only reason the view exists. So the numeric domain spans every ship, and a
 * categorical column's values get one bucket each across the grid - a value
 * only one ship ever reports still keeps its own colour everywhere.
 */
export type ColorPlan =
  | { kind: 'numeric'; colorBy: string; domain: NumericColorDomain }
  | {
      kind: 'discrete';
      colorBy: string;
      bucketOf: Map<number | string, number>;
      colors: string[];
      legend: Array<{ label: string; color: string }>;
    };

/** One numeric color domain for the whole comparison, never one per period. */
export function numericColorDomain(
  panels: Array<{ data: PlotResponse }>,
): NumericColorDomain | null {
  let min = Infinity;
  let max = -Infinity;
  let colorBy = '';
  for (const { data } of panels) {
    const type: string = data.plot_type;
    if ((type !== 'scatter' && type !== 'line') || !data.color_by || data.color_categorical) continue;
    colorBy ||= data.color_by;
    for (const value of data.color_values ?? []) {
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || !colorBy) return null;
  return { min, max: max === min ? min + 1 : max, colorBy };
}

/**
 * The colour plan for a grid of scatter panels, or null when there is nothing
 * to colour by.
 *
 * Mirrors the single chart's rule (`render-scatter.ts`), read across panels
 * rather than within one: a categorical column, or any "Discrete" scale, gives
 * every distinct value its own colour; a numeric column on a gradient scale
 * maps onto the ramp. A categorical column with more distinct values than there
 * are colours has no readable form either way, so colouring is dropped and the
 * points fall back to their series colour - the same thing the single chart does.
 */
export function colorPlanFor(
  panels: Array<{ data: PlotResponse }>,
  scaleName: string,
): ColorPlan | null {
  const coloured = panels.filter(
    (p) => (p.data.plot_type === 'scatter' || p.data.plot_type === 'line') && p.data.color_by,
  );
  if (!coloured.length) return null;
  const colorBy = coloured[0]!.data.color_by!;
  const values: Array<number | string | null> = [];
  let categorical = false;
  for (const { data } of coloured) {
    if (data.color_categorical) categorical = true;
    for (const value of data.color_values ?? []) {
      if (typeof value === 'string') categorical = true;
      values.push(value);
    }
  }
  if (!values.length) return null;

  if (categorical || isDiscreteScale(scaleName)) {
    const distinct = distinctColorValues(values, PLOT_COLORS.length);
    if (!distinct) return categorical ? null : numericPlan(coloured, colorBy);
    const palette = discreteColors(
      isDiscreteScale(scaleName) ? scaleName : DISCRETE_SCALE,
      distinct.length,
    );
    const colorAt = (i: number): string => palette[i] ?? PLOT_COLORS[i % PLOT_COLORS.length]!;
    return {
      kind: 'discrete',
      colorBy,
      bucketOf: new Map(distinct.map((value, i) => [value, i])),
      colors: distinct.map((_, i) => colorAt(i)),
      legend: distinct.map((value, i) => ({ label: formatColorValue(value), color: colorAt(i) })),
    };
  }
  return numericPlan(coloured, colorBy);
}

function numericPlan(
  panels: Array<{ data: PlotResponse }>,
  colorBy: string,
): ColorPlan | null {
  const domain = numericColorDomain(panels);
  return domain ? { kind: 'numeric', colorBy, domain } : null;
}

/** One panel's points, grouped into the plan's buckets. */
export function buildColorBuckets(
  values: Array<number | string | null>,
  plan: ColorPlan,
  scaleName: string,
): NumericBuckets {
  if (plan.kind === 'numeric') return buildNumericBuckets(values, plan.domain, scaleName);
  const indices: number[][] = plan.colors.map(() => []);
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value == null) continue;
    const bucket = plan.bucketOf.get(value);
    if (bucket != null) indices[bucket]!.push(i);
  }
  return { colors: plan.colors, indices };
}

/** Gradient resolution. 64 steps is smooth at any size a panel can be. */
const NUMERIC_BUCKETS = 64;

function buildNumericBuckets(
  values: Array<number | string | null>,
  domain: NumericColorDomain,
  scaleName: string,
): NumericBuckets {
  const count = NUMERIC_BUCKETS;
  const indices: number[][] = Array.from({ length: count }, () => []);
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const t = Math.max(0, Math.min(1, (value - domain.min) / (domain.max - domain.min)));
    indices[Math.min(count - 1, Math.floor(t * count))]!.push(i);
  }
  return {
    indices,
    colors: Array.from({ length: count }, (_, i) => colorScaleRgb(scaleName, (i + 0.5) / count)),
  };
}

export function drawBucketedScatterSeries(
  u: uPlot,
  seriesIndex: number,
  data: uPlot.AlignedData,
  scale: string,
  buckets: NumericBuckets,
  pointSize: number,
  alpha: number,
): void {
  if (u.series[seriesIndex]?.show === false) return;
  const ratio = window.devicePixelRatio || 1;
  const size = Math.max(2, pointSize) * ratio * 2;
  const half = size / 2;
  const xs = data[0] as number[];
  const ys = data[seriesIndex] as Array<number | null>;
  const xMax = u.bbox.left + u.bbox.width;
  const yMax = u.bbox.top + u.bbox.height;
  const previousAlpha = u.ctx.globalAlpha;
  u.ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  try {
    for (let bucket = 0; bucket < buckets.indices.length; bucket++) {
      const points = buckets.indices[bucket]!;
      if (!points.length) continue;
      u.ctx.fillStyle = buckets.colors[bucket]!;
      for (const index of points) {
        const x = xs[index];
        const y = ys[index];
        if (x == null || y == null || !Number.isFinite(x) || !Number.isFinite(y)) continue;
        const cx = u.valToPos(x, 'x', true);
        const cy = u.valToPos(y, scale, true);
        if (cx < u.bbox.left || cx > xMax || cy < u.bbox.top || cy > yMax) continue;
        u.ctx.fillRect(cx - half, cy - half, size, size);
      }
    }
  } finally {
    u.ctx.globalAlpha = previousAlpha;
  }
}



/**
 * Which bucket each point falls in, or null where it has no colour.
 *
 * Separate from `buildColorBuckets` because a line needs the answer *per point
 * in order*: its colour is carried through the same dedupe and gap-injection
 * the y values go through (see `render-line.ts`), and an index survives that
 * trip where a raw categorical string would not.
 */
export function pointBuckets(
  values: Array<number | string | null>,
  plan: ColorPlan,
): Array<number | null> {
  const out: Array<number | null> = new Array(values.length).fill(null);
  if (plan.kind === 'numeric') {
    const { min, max } = plan.domain;
    const span = max - min || 1;
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      const t = Math.max(0, Math.min(1, (value - min) / span));
      out[i] = Math.min(NUMERIC_BUCKETS - 1, Math.floor(t * NUMERIC_BUCKETS));
    }
    return out;
  }
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value == null) continue;
    out[i] = plan.bucketOf.get(value) ?? null;
  }
  return out;
}

/** The colour for every bucket the plan can produce, indexed by bucket. */
export function planColors(plan: ColorPlan, scaleName: string): string[] {
  if (plan.kind === 'discrete') return plan.colors;
  return Array.from({ length: NUMERIC_BUCKETS }, (_, i) =>
    colorScaleRgb(scaleName, (i + 0.5) / NUMERIC_BUCKETS),
  );
}

/**
 * A line whose colour changes along it.
 *
 * Each segment takes the colour of the point it starts from, so a run of high
 * wind reads as a stretch of colour rather than a scattering of dots - which is
 * the whole point of colouring a *line* rather than its markers. Segments are
 * stroked one at a time: they are not contiguous per colour, so there is no
 * path to batch, and a 3000-point line is 3000 short strokes, which is nothing
 * next to the 200k `fillRect`s a scatter already does.
 *
 * A segment with a null at either end is skipped, exactly as uPlot's own path
 * would break there - unless `spanGaps` is on, in which case the caller has
 * already decided the gaps are structural and the line joins across them.
 */
export function drawColoredLineSegments(
  u: uPlot,
  seriesIndex: number,
  data: uPlot.AlignedData,
  scale: string,
  buckets: Array<number | null>,
  colors: string[],
  width: number,
  spanGaps: boolean,
): void {
  if (u.series[seriesIndex]?.show === false) return;
  const xs = data[0] as number[];
  const ys = data[seriesIndex] as Array<number | null>;
  const ctx = u.ctx;
  const ratio = window.devicePixelRatio || 1;
  const left = u.bbox.left;
  const top = u.bbox.top;
  const right = left + u.bbox.width;
  const bottom = top + u.bbox.height;

  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, u.bbox.width, u.bbox.height);
  ctx.clip();
  ctx.lineWidth = Math.max(1, width) * ratio;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // `previous` walks the last point that had a value, so spanGaps can bridge a
  // run of nulls in one segment instead of dropping the line entirely.
  let previous = -1;
  for (let i = 0; i < xs.length; i++) {
    const y = ys[i];
    if (y == null || !Number.isFinite(y)) {
      if (!spanGaps) previous = -1;
      continue;
    }
    if (previous >= 0) {
      const x0 = u.valToPos(xs[previous]!, 'x', true);
      const y0 = u.valToPos(ys[previous] as number, scale, true);
      const x1 = u.valToPos(xs[i]!, 'x', true);
      const y1 = u.valToPos(y, scale, true);
      // Both ends off the same side: nothing of the segment can be visible.
      const offX = (x0 < left && x1 < left) || (x0 > right && x1 > right);
      const offY = (y0 < top && y1 < top) || (y0 > bottom && y1 > bottom);
      if (!offX && !offY) {
        const bucket = buckets[previous] ?? buckets[i];
        ctx.strokeStyle = (bucket == null ? null : colors[bucket]) ?? (u.series[seriesIndex]!.stroke as string) ?? '#888';
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
      }
    }
    previous = i;
  }
  ctx.restore();
}
